(() => {
  "use strict";

  const namespace = "GuruShopCartDrawer";
  if (window[namespace]?.version === 1) return;

  const drawerRootSelector = "[data-guru-cart-drawer]";
  const pageRootSelector = "[data-guru-cart-page]";
  const rootSelector = `${drawerRootSelector}, ${pageRootSelector}`;
  let inputModality = "pointer";
  const retryMutations = new Map();
  const focusRequests = new Map();
  const announcementRequests = new Map();
  const statusTimers = new WeakMap();
  const reducedMotionRuns = new WeakMap();
  const initializedRoots = new WeakSet();

  const cartRoot = (node) => node?.closest?.(rootSelector) || null;
  const commerceLeaseBusy = () => Boolean(
    window.GuruShopCommerceLease?.isLocked?.(),
  );
  const sectionIdFor = (root) => String(root?.dataset.guruCartSectionId || "").trim();
  const requestKeyFor = (root) => sectionIdFor(root) || String(root?.id || "");
  const isDrawerRoot = (root) => root?.matches?.(drawerRootSelector);
  const isPageRoot = (root) => root?.matches?.(pageRootSelector);
  const route = (path) => {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}${String(path).replace(/^\/+/, "")}`;
  };

  const announce = (root, message) => {
    const status = root?.querySelector("[data-guru-cart-status]");
    if (!status) return;
    window.clearTimeout(statusTimers.get(root));
    status.textContent = "";
    window.requestAnimationFrame(() => {
      if (!root.isConnected) return;
      status.textContent = message;
      statusTimers.set(root, window.setTimeout(() => {
        status.textContent = "";
        statusTimers.delete(root);
      }, 7000));
    });
  };

  const pendingPlanControls = (root) =>
    [...root.querySelectorAll('[data-guru-cart-plan-toggle][data-pending="true"]')];

  const quantityRules = (input) => {
    const minimum = Math.max(1, Number(input?.min) || 1);
    const step = Math.max(1, Number(input?.step) || 1);
    const maximumAttribute = input?.getAttribute?.("max");
    const hasMaximum = maximumAttribute !== null && String(maximumAttribute).trim() !== "";
    const requestedMaximum = Number(maximumAttribute);
    const hardMaximum = hasMaximum && Number.isFinite(requestedMaximum)
      ? Math.max(minimum, requestedMaximum)
      : minimum;
    const maximum = hasMaximum
      ? minimum + (Math.floor((hardMaximum - minimum) / step) * step)
      : null;
    return { minimum, maximum, step };
  };

  const quantityOnLattice = (input, value, direction = "nearest") => {
    const { minimum, maximum, step } = quantityRules(input);
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return minimum;
    const position = (numeric - minimum) / step;
    const rounded = direction === "down"
      ? Math.floor(position)
      : direction === "up"
        ? Math.ceil(position)
        : Math.round(position);
    const latticeValue = minimum + (rounded * step);
    const upperBound = maximum ?? Number.MAX_SAFE_INTEGER;
    return Math.max(minimum, Math.min(upperBound, latticeValue));
  };

  const setShadowFooterVisibility = (root) => {
    const footer = root?.shadowRoot?.querySelector('[part~="footer"]');
    if (!footer) return;
    const hasFooter = Boolean(root.querySelector('[slot="footer"]'));
    const hidden = !hasFooter
      || root.getAttribute("aria-busy") === "true"
      || root.dataset.guruCartError === "true";
    footer.hidden = hidden;
    footer.toggleAttribute("aria-hidden", hidden);
  };

  const mutationControls = (root) => [...root.querySelectorAll([
    "[data-guru-cart-quantity-action]",
    "[data-guru-cart-quantity]",
    "[data-guru-cart-plan-toggle]",
    "[data-guru-cart-frequency]",
    "[data-guru-cart-quick-add]",
  ].join(","))];

  const setMutationControlsLocked = (root, locked) => {
    for (const control of mutationControls(root)) {
      const supportsDisabled = "disabled" in control;
      if (locked) {
        if (!Object.hasOwn(control.dataset, "guruCartWasDisabled")) {
          control.dataset.guruCartWasDisabled = supportsDisabled && control.disabled ? "true" : "false";
          control.dataset.guruCartWasTabindex = control.getAttribute("tabindex") ?? "";
        }
        if (supportsDisabled) control.disabled = true;
        else {
          control.setAttribute("aria-disabled", "true");
          control.setAttribute("tabindex", "-1");
        }
      } else if (Object.hasOwn(control.dataset, "guruCartWasDisabled")) {
        if (supportsDisabled) control.disabled = control.dataset.guruCartWasDisabled === "true";
        else {
          control.removeAttribute("aria-disabled");
          const previousTabindex = control.dataset.guruCartWasTabindex;
          if (previousTabindex) control.setAttribute("tabindex", previousTabindex);
          else control.removeAttribute("tabindex");
        }
        delete control.dataset.guruCartWasDisabled;
        delete control.dataset.guruCartWasTabindex;
      }
    }
  };

  const memberCheckoutBusy = (root) => root?.dataset.minGuruCheckoutBusy === "true";
  const cartMutationBusy = (root) => root?.dataset.minGuruCartMutationBusy === "true";

  const blockMutationDuringCheckout = (root) => {
    const checkoutBusy = memberCheckoutBusy(root);
    const mutationBusy = commerceLeaseBusy()
      || cartMutationBusy(root)
      || root?.getAttribute("aria-busy") === "true";
    if (!checkoutBusy && !mutationBusy) return false;
    announce(root, checkoutBusy
      ? "Betalingen klargøres. Vent med at ændre kurven."
      : "Kurven opdateres. Vent et øjeblik.");
    return true;
  };

  const stopMotion = (root, frames = 2) => {
    if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const run = {};
    reducedMotionRuns.set(root, run);
    const finishFrame = (remaining) => window.requestAnimationFrame(() => {
      if (reducedMotionRuns.get(root) !== run) return;
      const targets = [root, root?.shadowRoot].filter(Boolean);
      for (const target of targets) {
        for (const animation of target.getAnimations?.({ subtree: true }) || []) {
          try { animation.finish(); } catch { animation.cancel(); }
        }
      }
      if (remaining > 1 && root.isConnected) finishFrame(remaining - 1);
      else reducedMotionRuns.delete(root);
    });
    finishFrame(Math.max(1, frames));
  };

  const ensureFocusInside = (root) => {
    const documentFocus = document.activeElement;
    const shadowFocus = root.shadowRoot?.activeElement;
    if (documentFocus === root || root.contains(documentFocus) || shadowFocus) return;
    root.querySelector("[data-guru-cart-initial-focus], [data-cart-continue], .cart-empty__action")
      ?.focus({ preventScroll: true });
  };

  const syncCheckoutState = (root) => {
    if (!root) return;
    const memberBusy = memberCheckoutBusy(root);
    const mutationBusy = cartMutationBusy(root);
    const leaseBusy = commerceLeaseBusy();
    const sessionResolving = root.dataset.minGuruSessionState === "resolving";
    const busy = root.getAttribute("aria-busy") === "true"
      || memberBusy
      || mutationBusy
      || leaseBusy;
    const failed = root.dataset.guruCartError === "true";
    const pending = pendingPlanControls(root).length > 0;
    const attention = root.querySelector("[data-guru-cart-line-blocking]") !== null;
    const blocked = busy || sessionResolving || failed || pending || attention;
    const checkout = root.querySelector("[data-cart-continue]");
    const label = root.querySelector("[data-guru-cart-checkout-label]");
    const lock = root.querySelector("[data-guru-cart-checkout-lock]");

    root.dataset.guruCartBlocked = String(blocked);
    setMutationControlsLocked(root, busy || failed);
    setShadowFooterVisibility(root);
    if (!checkout) return;
    checkout.disabled = blocked;
    checkout.setAttribute("aria-busy", String(busy));
    if (lock) {
      lock.toggleAttribute("hidden", blocked);
      lock.setAttribute("aria-hidden", "true");
    }

    if (label) {
      const readyLabel = label.dataset.guruCartCheckoutReadyLabel
        || "Gå til sikker betaling";
      label.textContent = busy
        ? "Opdaterer kurven …"
        : sessionResolving
          ? "Klargør betaling …"
        : failed || pending || attention
          ? "Løs ændringer for at fortsætte"
          : readyLabel;
    }
  };

  const setBusy = (root, busy) => {
    root.toggleAttribute("aria-busy", busy);
    if (busy) root.setAttribute("aria-busy", "true");
    const loading = root.querySelector("[data-guru-cart-loading]");
    const error = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    const failed = root.dataset.guruCartError === "true";
    if (loading) loading.hidden = !busy;
    if (error) error.hidden = busy || !failed;
    if (primary) primary.hidden = busy || failed;
    if (checkoutForm) checkoutForm.hidden = busy || failed;
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
  };

  const clearError = (root) => {
    root.dataset.guruCartError = "false";
    const error = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    if (error) error.hidden = true;
    if (primary && root.getAttribute("aria-busy") !== "true") primary.hidden = false;
    if (checkoutForm && root.getAttribute("aria-busy") !== "true") checkoutForm.hidden = false;
    retryMutations.delete(requestKeyFor(root));
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
  };

  const showError = (root, error, retry, { commerceLeaseRecovery = false } = {}) => {
    root.dataset.guruCartError = "true";
    const errorBox = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    const message = root.querySelector("[data-guru-cart-error-message]");
    const copy = typeof error?.description === "string"
      ? error.description
      : typeof error?.message === "string"
        ? error.message
        : "Din kurv er ikke ændret. Prøv igen.";
    if (message) message.textContent = copy;
    if (errorBox) {
      errorBox.hidden = false;
      errorBox.scrollIntoView({ block: "nearest" });
    }
    if (primary) primary.hidden = true;
    if (checkoutForm) checkoutForm.hidden = true;
    retryMutations.set(requestKeyFor(root), { commerceLeaseRecovery, retry });
    syncCheckoutState(root);
    announce(root, `Kurven kunne ikke opdateres. ${copy}`);
    window.requestAnimationFrame(() => {
      const retryButton = errorBox?.querySelector("[data-guru-cart-retry]");
      (retryButton || errorBox)?.focus({ preventScroll: true });
    });
    if (window.themeVariables?.settings?.cartType === "drawer") {
      void root.show?.();
    }
  };

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections },
      }),
    );
    return [...new Set(sections.filter(Boolean))];
  };

  const validCartPayload = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return false;
    const itemCount = Number(cart.item_count);
    const totalPrice = Number(cart.total_price);
    if (
      !Number.isSafeInteger(itemCount)
      || itemCount < 0
      || !Number.isSafeInteger(totalPrice)
      || totalPrice < 0
    ) return false;

    const keys = new Set();
    let quantityTotal = 0;
    for (const item of cart.items) {
      const key = String(item?.key || "");
      const quantity = Number(item?.quantity);
      if (
        !key
        || keys.has(key)
        || !Number.isSafeInteger(quantity)
        || quantity < 1
      ) return false;
      keys.add(key);
      quantityTotal += quantity;
    }
    return quantityTotal === itemCount;
  };

  const sectionLinesMatchCart = (primary, cart) => {
    const renderedLines = [...primary.querySelectorAll(
      "[data-guru-cart-lines] [data-cart-line-key], .cart-drawer__items [data-cart-line-key]",
    )];
    if (renderedLines.length !== cart.items.length) return false;

    const expected = new Map(
      cart.items.map((item) => [String(item.key), Number(item.quantity)]),
    );
    const rendered = new Map();
    for (const line of renderedLines) {
      const key = String(line.dataset.cartLineKey || "");
      const lineTotals = [...line.querySelectorAll("[data-min-guru-line-total]")];
      const quantity = Number(
        line.querySelector("[data-guru-cart-quantity]")?.value,
      );
      if (
        !key
        || rendered.has(key)
        || line.dataset.commerceIdentity !== key
        || lineTotals.length !== 1
        || lineTotals[0].dataset.commerceIdentity !== key
        || !Number.isSafeInteger(quantity)
        || quantity < 1
      ) return false;
      rendered.set(key, quantity);
    }
    return [...expected].every(
      ([key, quantity]) => rendered.get(key) === quantity,
    );
  };

  const parsedSectionRoot = (root, documentFragment) => {
    const sectionId = sectionIdFor(root);
    const section = documentFragment.getElementById(`shopify-section-${sectionId}`);
    if (!sectionId || !section) return { section: null, surface: null };
    if (isDrawerRoot(root)) {
      return {
        section,
        surface: section.querySelector("cart-drawer[data-guru-cart-drawer]"),
      };
    }
    if (isPageRoot(root)) {
      return {
        section,
        surface: section.querySelector("[data-guru-cart-page]"),
      };
    }
    return { section, surface: null };
  };

  const validSectionHtml = (root, html, cart) => {
    if (typeof html !== "string" || html.trim() === "") return "";
    const sectionId = sectionIdFor(root);
    if (!sectionId) return "";
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const { section, surface } = parsedSectionRoot(root, documentFragment);
    const drawer = isDrawerRoot(root) ? surface : null;
    const page = isPageRoot(root) ? surface : null;
    const content = surface?.querySelector("[data-cart-content]");
    const primary = content?.querySelector("[data-guru-cart-primary]");
    const loading = content?.querySelector("[data-guru-cart-loading]");
    const error = content?.querySelector(".cart-error[data-guru-cart-error]");
    const errorMessage = error?.querySelector("[data-guru-cart-error-message]");
    const retry = error?.querySelector("[data-guru-cart-retry]");
    const liveRegion = surface?.querySelector('[data-guru-cart-status][role="status"][aria-live="polite"]');
    const itemCount = Number(cart?.item_count);
    const footer = drawer?.querySelector('[slot="footer"]')
      || page?.querySelector("[data-guru-cart-checkout-form]");
    const checkoutForm = footer?.matches?.("[data-guru-cart-checkout-form]")
      ? footer
      : footer?.querySelector("[data-guru-cart-checkout-form]");
    const checkout = checkoutForm?.querySelector("[data-cart-continue]");
    const viewCart = checkoutForm?.querySelector("[data-cart-view]");
    const renderedItemCount = Number(surface?.dataset.cartItemCount);
    const renderedTotal = Number(surface?.dataset.cartNativeTotal);
    const renderedCurrency = String(surface?.dataset.cartCurrency || "").toUpperCase();
    const authoritativeCurrency = String(cart?.currency || "").toUpperCase();
    const showCheckout = surface?.dataset.cartShowCheckout;
    const showDiscount = surface?.dataset.cartShowDiscount;
    const showNote = surface?.dataset.cartShowNote;
    const showViewCart = surface?.dataset.cartShowViewCart;
    const showPrice = surface?.dataset.cartShowPriceInCheckout;
    const showShipping = surface?.dataset.cartShowShipping;
    const validConfiguration = [
      showCheckout,
      showDiscount,
      showNote,
      showViewCart,
      showPrice,
      showShipping,
    ]
      .every((value) => value === "true" || value === "false");
    if (
      !(isDrawerRoot(root)
        ? section?.matches("section.shopify-section.shopify-section--cart-drawer")
          && drawer?.matches("cart-drawer[data-guru-cart-drawer]")
          && drawer.getAttribute("template") === root.getAttribute("template")
          && drawer.querySelector('[slot="header"] [data-guru-cart-initial-focus]')
        : section?.matches("section.shopify-section.shopify-section--main-cart")
          && page?.matches("section.cart-panel[data-guru-cart-page]")
          && page.querySelector('.cart-panel__header [data-guru-cart-initial-focus]'))
      || !surface
      || surface.id !== root.id
      || surface.dataset.guruCartSectionId !== sectionId
      || surface.dataset.cartStorefrontOrigin !== root.dataset.cartStorefrontOrigin
      || surface.dataset.cartSurface !== root.dataset.cartSurface
      || !validConfiguration
      || !content
      || !primary
      || !loading
      || !loading.matches('[role="status"]')
      || !error
      || !error.matches('[role="alert"][tabindex="-1"]')
      || !errorMessage
      || !retry
      || !liveRegion
      || !Number.isFinite(itemCount)
      || renderedItemCount !== itemCount
      || renderedTotal !== Number(cart?.total_price)
      || !authoritativeCurrency
      || renderedCurrency !== authoritativeCurrency
      || (itemCount > 0 && !primary.querySelector(".cart-drawer__items"))
      || (itemCount > 0 && !sectionLinesMatchCart(primary, cart))
      || (itemCount === 0 && !primary.querySelector(".cart-empty .cart-empty__action"))
      || (itemCount === 0 && primary.querySelector("[data-cart-line-key]"))
      || (itemCount > 0 && (
        !footer
        || !checkoutForm
        || (showDiscount === "true") !== Boolean(checkoutForm.querySelector(".cart-summary__discount"))
        || (showNote === "true") !== Boolean(checkoutForm.querySelector('cart-note textarea[name="note"]'))
        || (showShipping === "true") !== Boolean(checkoutForm.querySelector(".cart-summary__shipping"))
        || (showCheckout === "true") !== Boolean(checkout?.matches('button[type="submit"][name="checkout"]'))
        || (showViewCart === "true") !== Boolean(viewCart?.matches('a[href][data-cart-view]'))
        || (showCheckout === "true" && !checkout.querySelector("[data-guru-cart-checkout-ready-label]"))
        || (showCheckout === "true" && checkout.querySelector("[data-guru-cart-checkout-ready-label]")?.dataset.cartCheckoutPrice !== showPrice)
      ))
    ) return "";
    return html;
  };

  const readCart = async () => {
    const response = await fetch(route("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw await responseError(response);
    const cart = await response.json();
    if (!validCartPayload(cart)) throw new Error("Shopify returnerede en ugyldig kurv.");
    return cart;
  };

  const readSection = async (root, cart) => {
    const sectionId = sectionIdFor(root);
    const sectionPath = isPageRoot(root) ? window.location.pathname : route("");
    const separator = sectionPath.includes("?") ? "&" : "?";
    const response = await fetch(`${sectionPath}${separator}section_id=${encodeURIComponent(sectionId)}`, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    });
    if (!response.ok) throw new Error("Kurvvisningen kunne ikke hentes.");
    const html = validSectionHtml(root, await response.text(), cart);
    if (!html) throw new Error("Shopify returnerede en ugyldig kurvvisning.");
    return html;
  };

  const cartWithSection = async (root, cart, sections) => {
    if (!validCartPayload(cart)) throw new Error("Shopify returnerede en ugyldig kurv.");
    const roots = [...document.querySelectorAll(rootSelector)];
    if (!roots.includes(root)) roots.push(root);
    for (const currentRoot of roots) {
      const sectionId = sectionIdFor(currentRoot);
      const sectionHtml = validSectionHtml(
        currentRoot,
        sections?.[sectionId],
        cart,
      );
      if (!sectionHtml) {
        throw new Error("Shopify returnerede en ufuldstændig kurvvisning.");
      }
    }
    return {
      ...cart,
      sections: { ...(cart.sections || {}), ...(sections || {}) },
    };
  };

  const readCurrentSections = async (cart) => {
    const entries = await Promise.all(
      [...document.querySelectorAll(rootSelector)].map(async (root) => [
        sectionIdFor(root),
        await readSection(root, cart),
      ]),
    );
    return Object.fromEntries(entries.filter(([sectionId]) => sectionId));
  };

  const waitForCartRefresh = (root, timeout = 2500) => new Promise((resolve, reject) => {
    let timer;
    const eventName = isPageRoot(root)
      ? "guru-cart-page:refreshed"
      : "cart-drawer:refreshed";
    const eventTarget = isPageRoot(root) ? document : root;
    const sectionId = sectionIdFor(root);
    const cleanup = () => {
      window.clearTimeout(timer);
      eventTarget.removeEventListener(eventName, onRefresh);
    };
    const onRefresh = (event) => {
      if (isPageRoot(root) && event.detail?.sectionId !== sectionId) return;
      cleanup();
      resolve(event.target);
    };
    eventTarget.addEventListener(eventName, onRefresh);
    timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("Kurvvisningen blev ikke opdateret. Prøv igen."));
    }, timeout);
  });

  const commitCartChange = async (root, cart, baseEvent, onSuccessDo = "") => {
    const refresh = waitForCartRefresh(root);
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent, onSuccessDo, cart },
      }),
    );
    await refresh;
    return cart;
  };

  const guardCartChange = (event) => {
    const roots = [...document.querySelectorAll(rootSelector)];
    if (!roots.length) return;
    const cart = event.detail?.cart;
    const invalidRoot = roots.find((root) => !(
      validCartPayload(cart)
      && validSectionHtml(
        root,
        cart?.sections?.[sectionIdFor(root)],
        cart,
      )
    ));
    if (!invalidRoot) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showError(
      invalidRoot,
      new Error("Shopify returnerede en ufuldstændig kurvvisning."),
      () => refreshOnly(invalidRoot, "Kurven er opdateret.", "guru-cart:recovery"),
    );
    document.dispatchEvent(new CustomEvent("guru-cart:change-rejected"));
  };

  window.addEventListener("cart:change", guardCartChange, true);

  document.addEventListener("cart:prepare-bundled-sections", (event) => {
    if (!Array.isArray(event.detail?.sections)) return;
    document.querySelectorAll(pageRootSelector).forEach((root) => {
      const sectionId = sectionIdFor(root);
      if (sectionId) event.detail.sections.push(sectionId);
    });
  });

  document.addEventListener("cart:change", (event) => {
    if (event.defaultPrevented) return;
    const cart = event.detail?.cart;
    document.querySelectorAll(pageRootSelector).forEach((root) => {
      const sectionId = sectionIdFor(root);
      const html = cart?.sections?.[sectionId];
      if (!validSectionHtml(root, html, cart)) return;
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const replacement = parsedSectionRoot(root, parsed).surface;
      if (!replacement) return;
      root.replaceWith(replacement);
      initializeRoot(replacement);
      replacement.dispatchEvent(new CustomEvent("guru-cart-page:refreshed", {
        bubbles: true,
        detail: { sectionId },
      }));
    });
  }, true);

  window.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    if (window.GuruNativeProductForm?.ownsRecoveryLease?.(form)) return;
    const root = document.querySelector(rootSelector);
    const cardBlocked = Boolean(
      form.closest("[data-guru-product-card-form]")
      && root
      && (
        root.getAttribute("aria-busy") === "true"
        || root.dataset.minGuruCartMutationBusy === "true"
        || root.dataset.guruCartError === "true"
      )
    );
    if (!commerceLeaseBusy() && !cardBlocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: { error: "Kurven opdateres. Vent et øjeblik." },
    }));
  }, true);

  const responseError = async (response) => {
    const data = await response.json().catch(() => ({}));
    const description = data.description || data.message || "Prøv igen om et øjeblik.";
    return { ...data, description };
  };

  const parseProperties = (value) => {
    try { return JSON.parse(value || "{}"); } catch { return {}; }
  };

  const normalizedPropertyEntries = (value) => {
    if (value === null || value === undefined) return [];
    const source = Array.isArray(value) ? value : Object.entries(value);
    const normalized = [];
    const keys = new Set();
    for (const entry of source) {
      if (!Array.isArray(entry) || entry.length < 2) return null;
      const key = String(entry[0] || "");
      const propertyValue = entry[1];
      if (!key || keys.has(key)) return null;
      keys.add(key);
      if (
        propertyValue === null
        || propertyValue === undefined
        || String(propertyValue).trim() === ""
      ) continue;
      normalized.push([key, String(propertyValue)]);
    }
    return normalized.sort(([left], [right]) => left.localeCompare(right));
  };

  const stableProperties = (value) => {
    const entries = normalizedPropertyEntries(value);
    return entries === null ? null : JSON.stringify(entries);
  };

  const cartCommerceIdentity = (item) => {
    const variantId = Number(item?.variant_id || item?.id);
    const sellingPlanId = String(
      item?.selling_plan_allocation?.selling_plan?.id || "",
    );
    const properties = stableProperties(item?.properties);
    if (
      !Number.isSafeInteger(variantId)
      || variantId < 1
      || (sellingPlanId && !/^\d+$/.test(sellingPlanId))
      || properties === null
    ) return "";
    return JSON.stringify([String(variantId), sellingPlanId, properties]);
  };

  const cartCommerceSnapshot = (cart) => {
    if (!validCartPayload(cart)) return null;
    const snapshot = new Map();
    for (const item of cart.items) {
      const identity = cartCommerceIdentity(item);
      if (!identity) return null;
      snapshot.set(
        identity,
        (snapshot.get(identity) || 0) + Number(item.quantity),
      );
    }
    return snapshot;
  };

  const expectedAddSnapshot = (baseline, variantId) => {
    if (!baseline || !Number.isSafeInteger(variantId) || variantId < 1) return null;
    const identity = JSON.stringify([String(variantId), "", "[]"]);
    const expected = new Map(baseline);
    expected.set(identity, (expected.get(identity) || 0) + 1);
    return expected;
  };

  const snapshotsMatch = (left, right) => Boolean(
    left
    && right
    && left.size === right.size
    && [...left].every(([identity, quantity]) => right.get(identity) === quantity),
  );

  const requestFocusAfterRefresh = (root, line, control, focusOptions = {}) => {
    focusRequests.set(requestKeyFor(root), {
      lineKey: line?.dataset.cartLineKey || "",
      variantId: line?.dataset.variantId || "",
      sellingPlanId: line?.dataset.sellingPlanId || "",
      properties: stableProperties(parseProperties(line?.dataset.lineProperties)),
      fallbackLineKey: focusOptions.fallbackLineKey || "",
      nextLineKey: focusOptions.nextLineKey || "",
      previousLineKey: focusOptions.previousLineKey || "",
      control,
    });
  };

  const returnedLineKey = (cart, request) => {
    const matches = (cart?.items || []).filter((item) => {
      const planId = String(item.selling_plan_allocation?.selling_plan?.id || "");
      return String(item.variant_id || "") === request.variantId
        && planId === request.sellingPlanId
        && stableProperties(item.properties) === request.properties;
    });
    return matches.length === 1 ? String(matches[0].key || "") : "";
  };

  const restoreFocus = (root) => {
    const requestKey = requestKeyFor(root);
    const request = focusRequests.get(requestKey);
    if (!request) return;
    focusRequests.delete(requestKey);
    const escaped = (value) => window.CSS?.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, "\\$&");
    if (request.control === "recommendation") {
      const recommendation = request.recommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.recommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      (recommendation || root.querySelector("[data-cart-continue]"))?.focus({ preventScroll: true });
      return;
    }
    if (request.control === "recommendation-pointer") {
      const recommendation = request.recommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.recommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      const fallbackRecommendation = request.fallbackRecommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.fallbackRecommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      (recommendation || fallbackRecommendation || root.querySelector("[data-cart-continue]"))
        ?.focus({ preventScroll: true });
      return;
    }
    if (request.control === "remove") {
      const nextLine = request.nextLineKey
        ? root.querySelector(`[data-cart-line-key="${escaped(request.nextLineKey)}"]`)
        : null;
      const previousLine = request.previousLineKey
        ? root.querySelector(`[data-cart-line-key="${escaped(request.previousLineKey)}"]`)
        : null;
      (nextLine?.querySelector("h2 a")
        || previousLine?.querySelector("h2 a")
        || root.querySelector(".cart-empty__action")
        || root.querySelector("[data-guru-cart-initial-focus]"))
        ?.focus({ preventScroll: true });
      return;
    }
    const line = root.querySelector(`[data-cart-line-key="${escaped(request.returnedLineKey || request.lineKey)}"]`)
      || (request.fallbackLineKey ? root.querySelector(`[data-cart-line-key="${escaped(request.fallbackLineKey)}"]`) : null);
    let target = null;

    if (line) {
      if (request.control === "frequency") target = line.querySelector("[data-guru-cart-frequency]");
      if (request.control === "toggle") target = line.querySelector("[data-guru-cart-plan-toggle]");
      if (request.control === "minus") target = line.querySelector('[data-guru-cart-quantity-action="minus"], [data-guru-cart-quantity-action="remove"]');
      if (request.control === "plus") {
        target = line.querySelector('[data-guru-cart-quantity-action="plus"]')
          || line.querySelector("[data-guru-cart-quantity]");
      }
      if (request.control === "quantity") target = line.querySelector("[data-guru-cart-quantity]");
      target ||= line.querySelector("h2 a, [data-guru-cart-quantity]");
    }

    target ||= root.querySelector("[data-cart-continue], .cart-empty__action");
    target?.focus({ preventScroll: true });
  };

  const refreshOnly = async (root, successCopy, baseEvent = "guru-cart:refresh") => {
    if (root.getAttribute("aria-busy") === "true" || memberCheckoutBusy(root)) return false;
    clearError(root);
    setBusy(root, true);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const cart = await readCart();
      const refreshedCart = await cartWithSection(root, cart, await readCurrentSections(cart));
      announcementRequests.set(requestKeyFor(root), successCopy);
      await commitCartChange(root, refreshedCart, baseEvent);
      return true;
    } catch (error) {
      showError(root, error, () => refreshOnly(root, successCopy, baseEvent));
      return false;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setBusy(root, false);
    }
  };

  const changeLine = async (root, mutation, successCopy, baseEvent = "line-item:change") => {
    if (root.getAttribute("aria-busy") === "true" || blockMutationDuringCheckout(root)) return;
    clearError(root);
    setBusy(root, true);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let mutationCommitted = false;

    try {
      const sections = bundledSections();
      const payload = {
        id: mutation.id,
        quantity: mutation.quantity,
        sections: sections.join(","),
        sections_url: window.location.pathname,
      };
      if (Object.hasOwn(mutation, "selling_plan")) payload.selling_plan = mutation.selling_plan;
      const focusRequest = focusRequests.get(requestKeyFor(root));
      if (focusRequest && Object.hasOwn(mutation, "selling_plan")) {
        focusRequest.sellingPlanId = String(mutation.selling_plan || "");
      }

      const response = await fetch(route("cart/change.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw await responseError(response);
      const responseCart = await response.json();
      mutationCommitted = true;
      const cart = await cartWithSection(root, responseCart, responseCart.sections);
      if (focusRequest) focusRequest.returnedLineKey = returnedLineKey(cart, focusRequest);
      announcementRequests.set(requestKeyFor(root), successCopy);
      await commitCartChange(root, cart, baseEvent);
    } catch (error) {
      const retry = mutationCommitted
        ? () => refreshOnly(root, successCopy, `${baseEvent}:refresh`)
        : () => changeLine(root, mutation, successCopy, baseEvent);
      showError(root, error, retry);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setBusy(root, false);
    }
  };

  const addVariant = async (root, button) => {
    if (root.getAttribute("aria-busy") === "true" || blockMutationDuringCheckout(root)) return;
    const variantId = Number(button.dataset.guruCartQuickAdd);
    if (!Number.isInteger(variantId) || variantId < 1) return;
    if (button.dataset.guruCartRequiresSellingPlan !== "false") {
      announce(root, "Produktet kræver et gyldigt leveringsinterval og kan ikke tilføjes som engangskøb.");
      return;
    }
    const recommendation = button.closest("[data-cart-recommendation]");
    const recommendationItem = recommendation?.closest("li");
    const nextRecommendation = recommendationItem?.nextElementSibling
      ?.querySelector?.("[data-cart-recommendation]");
    const recommendationFocus = inputModality === "keyboard"
      ? {
          control: "recommendation",
          recommendationProductId: nextRecommendation?.dataset.productId || "",
        }
      : {
          control: "recommendation-pointer",
          recommendationProductId: recommendation?.dataset.productId || "",
          fallbackRecommendationProductId: nextRecommendation?.dataset.productId || "",
        };

    clearError(root);
    setBusy(root, true);
    button.setAttribute("aria-busy", "true");
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    let postStarted = false;
    let additionVerified = false;

    try {
      const baselineCart = await readCart();
      const baseline = cartCommerceSnapshot(baselineCart);
      const expected = expectedAddSnapshot(baseline, variantId);
      if (!baseline || !expected) {
        throw new Error("Kurvens status kunne ikke bekræftes.");
      }
      const sections = bundledSections();
      const formData = new FormData();
      formData.set("id", String(variantId));
      formData.set("quantity", "1");
      formData.set("sections", sections.join(","));
      formData.set("sections_url", window.location.pathname);

      postStarted = true;
      const response = await fetch(route("cart/add.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: formData,
      });

      if (!response.ok) throw await responseError(response);
      const added = await response.json();
      const authoritativeCart = await readCart();
      if (!snapshotsMatch(cartCommerceSnapshot(authoritativeCart), expected)) {
        throw new Error("Shopify bekræftede ikke den valgte vare i kurven.");
      }
      additionVerified = true;
      focusRequests.set(requestKeyFor(root), recommendationFocus);
      const cart = await cartWithSection(root, authoritativeCart, added.sections);
      announcementRequests.set(
        requestKeyFor(root),
        "Produktet er lagt i kurven som engangskøb.",
      );
      await commitCartChange(root, cart, "variant:add");
    } catch (error) {
      const retry = postStarted
        ? () => refreshOnly(
            root,
            additionVerified ? "Produktet er lagt i kurven som engangskøb." : "Kurven er opdateret.",
            "variant:add:refresh",
          )
        : () => addVariant(root, button);
      showError(root, error, retry);
    } finally {
      button.removeAttribute("aria-busy");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setBusy(root, false);
    }
  };

  const togglePlan = (root, toggle) => {
    if (blockMutationDuringCheckout(root)) return;
    const line = toggle.closest("[data-cart-line-key]");
    const frequency = line?.querySelector("[data-guru-cart-frequency]");
    const frequencyWrap = frequency?.closest(".cart-frequency");
    if (!line || !frequency || !frequencyWrap) return;

    const committed = line.querySelector("[data-guru-cart-frequency] option:checked")?.value || "";
    const pending = toggle.dataset.pending === "true";
    const description = line.querySelector("[data-guru-cart-plan-description]");
    const shouldRestoreFocus = inputModality === "keyboard";

    if (pending) {
      delete toggle.dataset.pending;
      toggle.setAttribute("aria-checked", "false");
      toggle.setAttribute("aria-expanded", "false");
      frequency.value = "";
      frequency.disabled = true;
      frequencyWrap.hidden = true;
      line.querySelector("[data-cart-plan-state]")?.setAttribute("data-cart-plan-state", "single");
      if (description) description.textContent = "Aktuel købsmåde: Engangskøb.";
      syncCheckoutState(root);
      announce(root, "Fast levering blev ikke aktiveret.");
      if (shouldRestoreFocus) toggle.focus({ preventScroll: true });
      return;
    }

    if (committed) {
      requestFocusAfterRefresh(root, line, "toggle");
      void changeLine(
        root,
        { id: line.dataset.cartLineKey, quantity: Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1), selling_plan: null },
        "Produktet er skiftet til engangskøb.",
      );
      return;
    }

    toggle.dataset.pending = "true";
    toggle.setAttribute("aria-checked", "true");
    toggle.setAttribute("aria-expanded", "true");
    frequencyWrap.hidden = false;
    frequency.disabled = false;
    frequency.value = "";
    line.querySelector("[data-cart-plan-state]")?.setAttribute("data-cart-plan-state", "pending");
    if (description) {
      description.textContent = "Fast levering er valgt. Vælg interval for at gennemføre skiftet. Indtil da beregnes linjen som engangskøb.";
    }
    syncCheckoutState(root);
    announce(root, "Vælg leveringsinterval for at aktivere fast levering.");
    if (shouldRestoreFocus) frequency.focus({ preventScroll: true });
  };

  const changeFrequency = (root, select) => {
    if (blockMutationDuringCheckout(root)) return;
    const line = select.closest("[data-cart-line-key]");
    const sellingPlan = Number(select.value);
    if (!line || !Number.isInteger(sellingPlan) || sellingPlan < 1) {
      announce(root, "Vælg et gyldigt leveringsinterval.");
      return;
    }

    requestFocusAfterRefresh(root, line, "frequency");
    void changeLine(
      root,
      {
        id: line.dataset.cartLineKey,
        quantity: Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1),
        selling_plan: sellingPlan,
      },
      `Fast levering er opdateret til ${select.options[select.selectedIndex].text.replace(/ · spar.*$/, "").toLocaleLowerCase("da-DK")}.`,
    );
  };

  const changeQuantity = (root, line, quantity, control, focusOptions = {}) => {
    if (blockMutationDuringCheckout(root)) return;
    const input = line.querySelector("[data-guru-cart-quantity]");
    const bounded = Number(quantity) === 0
      ? 0
      : quantityOnLattice(input, quantity, control === "minus" ? "down" : control === "plus" ? "up" : "nearest");
    requestFocusAfterRefresh(root, line, control, focusOptions);
    void changeLine(
      root,
      { id: line.dataset.cartLineKey, quantity: bounded },
      bounded === 0 ? "Produktet er fjernet fra kurven." : `Antallet er opdateret til ${bounded}.`,
    );
  };

  document.addEventListener("pointerdown", () => {
    inputModality = "pointer";
  }, true);

  document.addEventListener("keydown", () => {
    inputModality = "keyboard";
  }, true);

  document.addEventListener("click", (event) => {
    const retry = event.target.closest?.("[data-guru-cart-retry]");
    if (retry) {
      event.preventDefault();
      const root = cartRoot(retry);
      const requestKey = requestKeyFor(root);
      const pendingRetry = retryMutations.get(requestKey);
      if (commerceLeaseBusy() && !pendingRetry?.commerceLeaseRecovery) {
        event.stopImmediatePropagation();
        announce(root, "Kurven opdateres. Vent et øjeblik.");
        return;
      }
      retryMutations.delete(requestKey);
      pendingRetry?.retry?.();
      return;
    }

    const quickAdd = event.target.closest?.("[data-guru-cart-quick-add]");
    if (quickAdd) {
      const root = cartRoot(quickAdd);
      if (!root) return;
      event.preventDefault();
      if (blockMutationDuringCheckout(root)) {
        event.stopImmediatePropagation();
        return;
      }
      void addVariant(root, quickAdd);
      return;
    }

    const planToggle = event.target.closest?.("[data-guru-cart-plan-toggle]");
    if (planToggle) {
      const root = cartRoot(planToggle);
      if (!root) return;
      event.preventDefault();
      if (blockMutationDuringCheckout(root)) {
        event.stopImmediatePropagation();
        return;
      }
      togglePlan(root, planToggle);
      return;
    }

    const quantityAction = event.target.closest?.("[data-guru-cart-quantity-action]");
    if (quantityAction) {
      const root = cartRoot(quantityAction);
      const line = quantityAction.closest("[data-cart-line-key]");
      if (!root || !line) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (blockMutationDuringCheckout(root)) return;
      const current = Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1);
      const type = quantityAction.dataset.guruCartQuantityAction;
      const lines = [...root.querySelectorAll("[data-cart-line-key]")];
      const index = lines.indexOf(line);
      const nextLineKey = lines[index + 1]?.dataset.cartLineKey || "";
      const previousLineKey = lines[index - 1]?.dataset.cartLineKey || "";
      const input = line.querySelector("[data-guru-cart-quantity]");
      const { step } = quantityRules(input);
      if (type === "remove") {
        changeQuantity(root, line, 0, "remove", { nextLineKey, previousLineKey });
      }
      if (type === "minus") changeQuantity(root, line, current - step, "minus");
      if (type === "plus") changeQuantity(root, line, current + step, "plus");
    }
  }, true);

  document.addEventListener("change", (event) => {
    const frequency = event.target.closest?.("[data-guru-cart-frequency]");
    if (frequency) {
      const root = cartRoot(frequency);
      if (root) {
        if (blockMutationDuringCheckout(root)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        changeFrequency(root, frequency);
      }
      return;
    }

    const quantity = event.target.closest?.("[data-guru-cart-quantity]");
    if (quantity) {
      const root = cartRoot(quantity);
      const line = quantity.closest("[data-cart-line-key]");
      if (!root || !line) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (blockMutationDuringCheckout(root)) return;
      const bounded = quantityOnLattice(quantity, quantity.value);
      quantity.value = String(bounded);
      changeQuantity(root, line, bounded, "quantity");
    }
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.("[data-guru-cart-checkout-form]");
    const root = cartRoot(form);
    if (!form || !root || event.submitter?.name !== "checkout") return;
    syncCheckoutState(root);
    if (root.dataset.guruCartBlocked !== "true") return;
    event.preventDefault();
    event.stopImmediatePropagation();
    announce(root, pendingPlanControls(root).length
      ? "Vælg leveringsinterval, før du går til betaling."
      : root.querySelector("[data-guru-cart-line-blocking]")
        ? "Fjern utilgængelige varer, før du går til betaling."
        : "Kurven skal opdateres, før du går til betaling.");
  }, true);

  const onCartRootRefreshed = (event) => {
    const root = event.target.matches?.(rootSelector) ? event.target : cartRoot(event.target);
    if (!root) return;
    root.dataset.guruCartError = "false";
    setBusy(root, false);
    syncCheckoutState(root);
    window.requestAnimationFrame(() => {
      restoreFocus(root);
      const requestKey = requestKeyFor(root);
      const announcement = announcementRequests.get(requestKey);
      if (announcement) {
        announcementRequests.delete(requestKey);
        announce(root, announcement);
      }
    });
  };

  document.addEventListener("cart-drawer:refreshed", onCartRootRefreshed);
  document.addEventListener("guru-cart-page:refreshed", onCartRootRefreshed);
  document.addEventListener("guru:commerce-lease:change", () => {
    document.querySelectorAll(rootSelector).forEach(syncCheckoutState);
  });

  const initializeRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    if (isDrawerRoot(root)) {
      root.addEventListener("dialog:before-show", () => stopMotion(root, 120));
      root.addEventListener("dialog:after-show", () => {
        stopMotion(root, 120);
        window.requestAnimationFrame(() => ensureFocusInside(root));
      });
      root.addEventListener("dialog:before-hide", () => stopMotion(root, 120));
    }
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
    stopMotion(root);
  };

  for (const cartEvent of ["cart:change", "cart:refresh"]) {
    document.addEventListener(cartEvent, () => {
      document.querySelectorAll(rootSelector).forEach((root) => stopMotion(root, 120));
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.matches?.(rootSelector)) initializeRoot(event.target);
    event.target.querySelectorAll?.(rootSelector).forEach(initializeRoot);
  });

  document.querySelectorAll(rootSelector).forEach(initializeRoot);

  window[namespace] = {
    version: 1,
    refresh: () => document.querySelectorAll(rootSelector).forEach(syncCheckoutState),
    prepareCart: async (cart, sections) => {
      const root = document.querySelector(rootSelector);
      if (!root) throw new Error("Kurvvisningen findes ikke.");
      return cartWithSection(root, cart, sections);
    },
    validateCart: (cart, sections) => {
      const root = document.querySelector(rootSelector);
      return Boolean(
        root
        && validCartPayload(cart)
        && validSectionHtml(root, sections?.[sectionIdFor(root)], cart),
      );
    },
    commitCart: async ({ cart, baseEvent = "variant:add", onSuccessDo = "" } = {}) => {
      const root = document.querySelector(rootSelector);
      if (!root) throw new Error("Kurvvisningen findes ikke.");
      return commitCartChange(root, cart, baseEvent, onSuccessDo);
    },
    showError: (error, retry, options) => {
      const root = document.querySelector(rootSelector);
      if (root) showError(root, error, retry, options);
    },
    clearError: () => {
      const root = document.querySelector(rootSelector);
      if (root) clearError(root);
    },
    isMutationPending: () => {
      const root = document.querySelector(rootSelector);
      return commerceLeaseBusy() || Boolean(root && (
        root.getAttribute("aria-busy") === "true"
        || root.dataset.minGuruCartMutationBusy === "true"
        || root.dataset.guruCartError === "true"
      ));
    },
  };
})();
